// =============================================================================
// PTEL - Role Expiration Service
// =============================================================================
// Comprobación y aplicación de expiración de roles.
// FB.10.04 · checkAndEnforceExpiration, getRemainingTime
// =============================================================================

import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, isConfigured, COLLECTIONS } from '../../config/firebase';
import type { FirebaseUser } from '../../types/firebase.types';
import { logRoleExpiration } from './audit.service';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface RemainingTime {
  /** true si el rol ya ha expirado */
  expired: boolean;
  /** Milisegundos restantes (0 si expirado, Infinity si indefinido) */
  remainingMs: number;
  /** Días restantes redondeados hacia abajo (0 si expirado, null si indefinido) */
  remainingDays: number | null;
  /** true si quedan ≤ 3 días (aviso próximo a expirar) */
  expiringSoon: boolean;
  /** Fecha de expiración original (null si indefinido/no aplica) */
  expiresAt: Date | null;
}

// Umbral «expira pronto»: 5 días en ms
const EXPIRING_SOON_MS = 5 * 24 * 60 * 60 * 1000;

// -----------------------------------------------------------------------------
// getRemainingTime
// -----------------------------------------------------------------------------

/**
 * Calcula el tiempo restante del rol del usuario.
 *
 * Reglas:
 * - `roleExpiresAt === null`      → indefinido (nunca expira)
 * - `roleExpiresAt === undefined`  → no aplica (viewer, sin expiración asignada)
 * - `roleExpiresAt` es Date       → se calcula diferencia con `now`
 *
 * @param userData - Datos del usuario con campos de expiración
 * @param now - Fecha actual inyectable para tests (por defecto: new Date())
 */
export function getRemainingTime(
  userData: Pick<FirebaseUser, 'roleExpiresAt'>,
  now: Date = new Date(),
): RemainingTime {
  const { roleExpiresAt } = userData;

  // Indefinido o no aplica → nunca expira
  if (roleExpiresAt === null || roleExpiresAt === undefined) {
    return {
      expired: false,
      remainingMs: Infinity,
      remainingDays: null,
      expiringSoon: false,
      expiresAt: null,
    };
  }

  const diffMs = roleExpiresAt.getTime() - now.getTime();

  if (diffMs <= 0) {
    return {
      expired: true,
      remainingMs: 0,
      remainingDays: 0,
      expiringSoon: false,
      expiresAt: roleExpiresAt,
    };
  }

  return {
    expired: false,
    remainingMs: diffMs,
    remainingDays: Math.floor(diffMs / (24 * 60 * 60 * 1000)),
    expiringSoon: diffMs <= EXPIRING_SOON_MS,
    expiresAt: roleExpiresAt,
  };
}

// -----------------------------------------------------------------------------
// checkAndEnforceExpiration
// -----------------------------------------------------------------------------

export interface ExpirationResult {
  /** true si el rol fue degradado a viewer */
  wasDowngraded: boolean;
  /** Información de tiempo restante */
  remaining: RemainingTime;
}

/**
 * Comprueba si el rol del usuario ha expirado y, de ser así, lo degrada a viewer
 * actualizando Firestore.
 *
 * Solo actúa sobre usuarios con rol 'editor' y roleExpiresAt definido (no null).
 * Los admin nunca se degradan. Los viewer no necesitan comprobación.
 *
 * @param userData - Datos completos del usuario
 * @param now - Fecha actual inyectable para tests
 * @returns Resultado con indicador de degradación y tiempo restante
 */
export async function checkAndEnforceExpiration(
  userData: Pick<FirebaseUser, 'uid' | 'role' | 'roleExpiresAt'>,
  now: Date = new Date(),
): Promise<ExpirationResult> {
  const remaining = getRemainingTime(userData, now);

  // Solo degradar editors con expiración definida y vencida
  if (userData.role !== 'editor' || !remaining.expired) {
    return { wasDowngraded: false, remaining };
  }

  // Escribir degradación en Firestore
  if (isConfigured && db) {
    const userRef = doc(db, COLLECTIONS.USERS, userData.uid);
    await updateDoc(userRef, {
      role: 'viewer',
      roleExpiresAt: null,
      roleGrantedAt: serverTimestamp(),
    });
    // Audit: registrar expiración de rol
    void logRoleExpiration(userData.uid, '', userData.role);
  }

  return { wasDowngraded: true, remaining };
}
